import { useCallback, useEffect, useRef } from 'react'
import { useDispatchAsync } from '@src/utils/dispatch'
import { WorkflowRun, WorkflowsRunState } from '@src/types'
import Storage from '@utils/storage.ts'

const DEFAULT_MAX_RETRY_COUNT = 15
const DEFAULT_ERROR_MESSAGE = 'Request failed, please try again'
const POLL_INTERVAL_MS = 3000

type WorkflowRunner = () => Promise<WorkflowRun>

interface RunWorkflowOptions {
  maxRetryCount?: number
}

interface RunnerConfig {
  idempotencyKey?: string
}

interface PollWorkflowParams {
  timeoutRef: React.RefObject<NodeJS.Timeout | undefined>
  retryCount: number
  maxRetryCount: number
  doRun: () => Promise<WorkflowRun>
  onSuccess: (run: WorkflowRun) => void
  onError: (error: any) => void
}

const pollWorkflowFinished = async ({
  timeoutRef,
  retryCount,
  maxRetryCount,
  doRun,
  onSuccess,
  onError,
}: PollWorkflowParams) => {
  const scheduleNextPoll = () => {
    // eslint-disable-next-line no-param-reassign
    timeoutRef.current = setTimeout(() => {
      pollWorkflowFinished({
        timeoutRef,
        retryCount: retryCount + 1,
        maxRetryCount,
        doRun,
        onSuccess,
        onError,
      })
    }, POLL_INTERVAL_MS)
  }

  try {
    const run = await doRun()

    if (run.state === WorkflowsRunState.Completed || run.state === WorkflowsRunState.Failed) {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current)
      }
      onSuccess(run)
      return
    }

    if (retryCount >= maxRetryCount) {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current)
      }
      onError(DEFAULT_ERROR_MESSAGE)
      return
    }
    scheduleNextPoll()
  } catch (error) {
    if (retryCount >= maxRetryCount) {
      onError(error)
      return
    }
    scheduleNextPoll()
  }
}

export const useRunWorkflow = ({
  maxRetryCount = DEFAULT_MAX_RETRY_COUNT,
}: RunWorkflowOptions = {}) => {
  const dispatchAsync = useDispatchAsync()
  const timeoutRef = useRef<NodeJS.Timeout | undefined>(undefined)

  useEffect(
    () => () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current)
      }
    },
    [],
  )

  const runWorkflow = useCallback(
    (runId: string) =>
      new Promise<WorkflowRun>((resolve, reject) =>
        pollWorkflowFinished({
          timeoutRef,
          retryCount: 0,
          maxRetryCount,
          doRun: () => dispatchAsync<WorkflowRun>({ type: 'app/workflowRun', payload: { runId } }),
          onSuccess: resolve,
          onError: reject,
        }),
      ),
    [dispatchAsync, maxRetryCount],
  )

  return useCallback(
    async (runner: WorkflowRunner, { idempotencyKey }: RunnerConfig = {}) => {
      if (idempotencyKey) {
        const runId = Storage.get<string>(idempotencyKey)
        if (runId) {
          return runWorkflow(runId)
        }
      }

      const workflow = await runner()

      if (idempotencyKey) {
        Storage.set(idempotencyKey, workflow.runId)
      }

      return runWorkflow(workflow.runId)
    },
    [runWorkflow],
  )
}
